import { withJsonFormsControlProps } from "@jsonforms/react";
import { Button, Checkbox, InputItem, LoadingSpinner, Tag } from "pepsico-ds";
import { useContext, useEffect, useMemo, useState } from "react";
import { KeywordGroupBrandSelectionContext } from "../../../../../context/KeywordGroupBrandSelectionContext";
import { searchHandler } from "../../../../../utils/functions";
import ButtonGroupDynamic from "../../../../common/ButtonGroupDynamic";
import Table from "../../../../common/table/Table";
import "./brand-select.scss";
import { BrandCard } from "./brandCard/BrandCard";

const SkuBrandSelectComponent = ({ data, path, handleChange }) => {
  const {
    loading,
    keywordGroupsBrandsData,
    availableBrands,
    setPreviousSelectedBrands,
  } = useContext(KeywordGroupBrandSelectionContext);

  const [viewType, setViewType] = useState("grid");
  const [keywordText, setKeywordText] = useState("");

  useEffect(() => {
    setPreviousSelectedBrands(data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const combinedItems = useMemo(() => {
    return Array.from(new Set([...(data || []), ...availableBrands]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brandsData = keywordGroupsBrandsData
    ? combinedItems
        ?.map((brandId) => {
          const brand = keywordGroupsBrandsData?.find((item) => item.id === brandId);
          if (brand) {
            const isSelected = data?.includes(brand.id);
            return {
              id: brand.id,
              title: brand.name,
              image: brand.imageUrl || "/assets/images/placeholder.jpg",
              selected: isSelected,
              checked: (
                <Checkbox checked={isSelected} onChange={() => onSelect(brand.id)} />
              ),
              sku: brand.keywords?.[0] || "-",
            };
          }
          return null;
        })
        .filter(Boolean)
        .filter((product) => product.title !== "Not Applicable")
    : [];

  const brandsDataList = keywordText
    ? searchHandler(keywordText, ["title"], brandsData)
    : brandsData;

  const selectedBrandsData = brandsData?.filter((product) => product.selected) || [];

  const headerData = [
    {
      header: "",
      columnKey: "checked",
    },
    {
      header: "Brand",
      columnKey: "title",
    },
    {
      header: "ID",
      columnKey: "id",
    },
  ];

  const onSelect = (brandId) => {
    const updatedData = data?.includes(brandId)
      ? data.filter((id) => id !== brandId)
      : [...(data || []), brandId];
    handleChange(path, updatedData);
  };

  const onSearch = (e) => {
    setKeywordText(e.target.value);
  };

  const onRemoveAll = () => {
    handleChange(path, []);
  };

  return (
    <div className="brand-select-wrapper">
      <div className="header">
        <span className="font-heading-h6-bold">Available Brands</span>
      </div>
      <div className="helpers">
        <ButtonGroupDynamic
          items={[
            {
              icon: "view_module",
              onClick: () => setViewType("grid"),
            },
            {
              icon: "view_list",
              onClick: () => setViewType("list"),
            },
          ]}
          size="medium"
          spacing="2px"
          variant="toggle"
          selectedIndex={viewType === "grid" ? 0 : 1}
        />
      </div>

      <div className="filter-container">
        <div className="content">
          <InputItem
            placeholder="Search by Brand Name"
            trailingIcon="search"
            size="medium"
            className="input-search"
            onChange={onSearch}
            value={keywordText}
          />
        </div>
      </div>
      <div className="brands-content">
        {loading && (
          <div className="loading-container">
            <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
          </div>
        )}

        {!loading &&
          (viewType === "grid" ? (
            <div className="brands-grid">
              {brandsDataList.length === 0 ? (
                <p>No available data.</p>
              ) : (
                brandsDataList?.map((brand) => (
                  <BrandCard
                    key={brand.id}
                    brand={brand}
                    selected={brand.selected}
                    onSelect={onSelect}
                  />
                ))
              )}
            </div>
          ) : (
            <div className="brands-table">
              <Table headerData={headerData} rowData={brandsDataList} />
            </div>
          ))}
        <div className="selected-brands-container">
          <div className="selected-header">
            <span className="font-xsmall-bold">{data?.length || 0} selected</span>
            <Button
              size="small"
              variant="tertiary"
              text="Remove all"
              onClick={onRemoveAll}
            />
          </div>
          <div className="brand-tags">
            {selectedBrandsData.map((brand) => (
              <Tag
                key={brand.id}
                text={[brand.title]}
                isRemovable={true}
                isCopyable={false}
                color="Auxiliary-600"
                onRemove={() => onSelect(brand.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SkuBrandSelectControl = withJsonFormsControlProps(
  SkuBrandSelectComponent
);
